import { useMemo, useState } from 'react';
import { useLocalStorageByUser } from 'common/hooks';
import { DEFAULT_JOBS_COLOR_PALETTE, JOBS_COLOR_PALETTE_KEY, JOBS_COLOR_PALETTE_PRESETS_KEY, JobsColorPalette } from 'dashboard409a/pages/Workboard/common/constants';

export type ColorPalettePreset = {
  name: string;
  palette: JobsColorPalette;
};

let cachedJobsColorPalette: JobsColorPalette = DEFAULT_JOBS_COLOR_PALETTE;

export const getJobsColorPalette = (): JobsColorPalette => cachedJobsColorPalette;

/**
 * Custom hook for managing color palette state and operations
 * @returns Object containing colorPalette, presets, and handler functions
 */
export const useJobsColorPalette = () => {
  const [storedColorPalette, setColorPalette, isReady] = useLocalStorageByUser<JobsColorPalette>(
    JOBS_COLOR_PALETTE_KEY,
    DEFAULT_JOBS_COLOR_PALETTE
  );
  const colorPalette = useMemo(
    () => {
      const next = { ...DEFAULT_JOBS_COLOR_PALETTE, ...storedColorPalette };
      // Update synchronously during render so non-hook consumers (e.g. styled components
      // calling getJobsColorPalette()) see the current palette before any child renders.
      cachedJobsColorPalette = next;
      return next;
    },
    [storedColorPalette]
  );
  const [presets, setPresets] = useLocalStorageByUser<ColorPalettePreset[]>(
    JOBS_COLOR_PALETTE_PRESETS_KEY,
    []
  );
  const [savePresetOpen, setSavePresetOpen] = useState(false);
  const [presetName, setPresetName] = useState('');

  const handleColorChange = (key: keyof JobsColorPalette, color: string) => {
    setColorPalette(prev => {
      const next = { ...prev, [key]: color };
      cachedJobsColorPalette = next;
      return next;
    });
  };

  const handleReset = () => {
    cachedJobsColorPalette = DEFAULT_JOBS_COLOR_PALETTE;
    setColorPalette(DEFAULT_JOBS_COLOR_PALETTE);
  };

  const handleSavePreset = () => {
    if (presetName.trim()) {
      const existingIndex = presets.findIndex(p => p.name === presetName.trim());
      const updatedPresets = existingIndex >= 0
        ? presets.map((p, idx) => idx === existingIndex ? { name: presetName.trim(), palette: colorPalette } : p)
        : [...presets, { name: presetName.trim(), palette: colorPalette }];
      setPresets(updatedPresets);
      setPresetName('');
      setSavePresetOpen(false);
    }
  };

  const handleOpenSavePreset = () => {
    setSavePresetOpen(true);
  };

  const handleCloseSavePreset = () => {
    setSavePresetOpen(false);
    setPresetName('');
  };

  const handleApplyPreset = (name: string) => {
    const preset = presets.find(p => p.name === name);
    if (preset) {
      setColorPalette(preset.palette);
      cachedJobsColorPalette = preset.palette;
    }
  };

  const handleDeletePreset = (name: string) => {
    setPresets(presets.filter(p => p.name !== name));
  };

  return {
    colorPalette,
    isReady,
    presets,
    savePresetOpen,
    presetName,
    setPresetName,
    handleColorChange,
    handleReset,
    handleSavePreset,
    handleOpenSavePreset,
    handleCloseSavePreset,
    handleApplyPreset,
    handleDeletePreset,
  };
};
